<template>
  <div class="modal-card custom-height">
    <b-loading :isFullPage="false" :active="isLoading" canCancel />
    <header class="modal-card-head spacing-pb-10">
      <p class="modal-card-title is-size-5 has-text-centered">
        {{ cardTitle }}
      </p>
      <div class="close-button cursor-pointer" @click.stop="$emit('close')">
        <b-icon icon="close" />
      </div>
    </header>

    <div class="modal-content-container has-background-white">
      <template>
        <section class="modal-card-body spacing-pt-10">
          <!-- eslint-disable vue/no-v-html -->
          <span
            class="has-text-weight-medium"
            v-html="General.TEXTS.DETAIL_SIGNATURE_HELP"
          />
          <div class="custom-title">
            <span class="has-text-weight-semibold">
              {{ General.SELECT_CERTIFICATE_SIGN }}:
            </span>
          </div>
          <div class="wrapper-list-cert">
            <ul>
              <li
                v-for="(cert, index) in getListCerts"
                :key="index"
                class="custom-pointer"
                :class="
                  `${
                    selectedCertificate === cert.thumbprint
                      ? 'selected-cert'
                      : ''
                  }`
                "
                @click="selectCertificateSign(cert)"
              >
                <div class="block">
                  <b-icon type="is-primary" icon="lock" size="is-small" />
                  <span>
                    {{ cert.subjectName }} - {{ cert.pkiBrazil.cpf }} ({{
                      cert.issuerName
                    }})
                  </span>
                </div>
              </li>
            </ul>
          </div>
        </section>
        <footer class="modal-card-foot">
          <div class="custom-button-docsign">
            <b-button
              :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
              :disabled="
                selectedCertificate === '' ||
                  isLoading ||
                  !getTokensExternalSignDocuments
              "
              class="column is-4 is-offset-4 is-flex-direction-column"
              @click.stop="sign"
            >
              <span class="is-size-6">{{ General.SIGN.toUpperCase() }}</span>
            </b-button>
          </div>
        </footer>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";

import { Init, SignDocuments } from "@/services/lacuna/LacunaSigner";
import { App, General } from "@/utils/constants";
import ModalWarning from "@/views/App/ModalWarning.vue";

const subscriptionType = {
  DIGITAL_SIGNATURE: "DIGITAL_SIGNATURE"
};

const listType = {
  SIGN_INDIVIDUAL_FILE: "INDIVIDUAL",
  BATCH_SIGN_FILES: "BATCH"
};

export default {
  name: "ModalExternalSignDocument",

  props: {
    hash: {
      type: String,
      required: true
    },
    currentType: {
      type: String,
      default: "INDIVIDUAL"
    },
    onClose: {
      type: Function,
      default: null
    }
  },

  data() {
    return {
      App,
      General,
      isLoading: false,
      cardTitle: General.SIGN,
      subscriptionType,
      listCerts: [],
      selectedCertificate: "",
      listType
    };
  },

  computed: {
    ...mapGetters({
      getTokensExternalSignDocuments: "getTokensExternalSignDocuments",
      isContrast: "isContrast"
    }),

    getListCerts() {
      return this.listCerts;
    },

    getDocumentTokens() {
      return {
        token: this.getTokensExternalSignDocuments.token,
        thumbprint: this.selectedCertificate,
        process_file_id: this.getTokensExternalSignDocuments.process_file_id
      };
    }
  },

  beforeMount() {
    this.validateSignature();
  },

  methods: {
    ...mapActions({
      postExternalSignatureResponseAction:
        "postExternalSignatureResponseAction",
      checkValidCertificate: "checkValidCertificate"
    }),
    ...mapMutations({
      setSelectedCertificate: "setSelectedCertificate"
    }),

    close() {
      this.onClose && this.onClose();
      this.$parent.close();
    },

    onWebPkiReady(certs) {
      this.listCerts = certs;
    },

    async selectCertificateSign(cert) {
      if (await this.checkValidCertificate({ cert })) {
        this.selectedCertificate = cert.thumbprint;
        this.setSelectedCertificate(cert.thumbprint);
      }
    },

    async sign() {
      if (this.currentType === this.listType.BATCH_SIGN_FILES) {
        this.openAlertMessage(
          this.General.MESSAGES.BATCH_SUBSCRIPTION_PROCESSING,
          "is-warning"
        );
      }

      await SignDocuments([this.getDocumentTokens], async document => {
        this.isLoading = true;
        this.$parent.canCancel = [];
        await this.postExternalSignatureResponseAction({
          hash: this.hash,
          refuse_options: document[0]
        });

        this.isLoading = false;
        this.close();
      });
    },

    openAlertMessage(message, type = "is-danger", duration = 8000) {
      this.snackToast.open({
        message: message,
        type: type,
        duration: duration
      });
    },

    onNotInstalled(pki) {
      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: this.General.WARNING,
          message: this.General.MESSAGES.WITHOUT_RESPKI,
          confirm: pki.redirectToInstallPage
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    async validateSignature() {
      this.isLoading = true;
      await this.$store.dispatch("externalTokensRequest", this.hash);
      await Init(this.onWebPkiReady, this.onNotInstalled);
      this.cardTitle = this.General.SIGNATURE_WITH;
      this.isLoading = false;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";

.modal-card {
  min-width: 800px;
  @media only screen and (max-width: $tablet) {
    width: 100%;
    min-width: auto;
  }
}

.modal-card-body {
  padding: 0 30px;
}

.modal-card-foot {
  padding: 15px 30px;
}

.modal-card-head,
.modal-card-foot {
  background-color: $white;
  border: none;
}

.wrapper-content {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: stretch;
  gap: 30px;

  @media only screen and (max-width: $tablet) {
    flex-direction: column;
    align-items: center;
  }
}

.wrapper-content div.box-left,
.wrapper-content div.box-right {
  border: 2px solid #84e2d2;
  border-radius: 5px;
  padding: 10px 30px;
  width: 100%;
  max-width: 550px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.box-content {
  width: 100%;
  max-width: 350px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 25px;
  background-color: $blue-oil;
  border-radius: 5px;
  flex: 1;
  align-self: center;
}

.cert_img {
  display: flex;
  min-height: 120px;
  min-width: 100%;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  background-color: $white;
  border-radius: 6px;
  border: 2px solid $primary;

  align-self: center;

  img {
    padding: 10px;
    margin: auto 0;
  }
}

.cert_img::after {
  content: "";
  width: 100%;
  height: 40px;
  background-color: $custom-color-add5cd;
  border-radius: 0 0 5px 5px;
}

.wrapper-content div.box-left > div.box-footer,
.wrapper-content div.box-right > div.box-footer {
  padding: 10px 0 0 0;
}

.wrapper-content div.box-footer .custom-buttonl-docsign,
.wrapper-content div.box-footer .custom-buttonr-docsign {
  text-align: right;
  padding: 10px 0 0 0;
}

footer.modal-card-foot > div.custom-button-docsign {
  width: 100%;
  text-align: right;
  padding: 0 0 15px 0;
}

.wrapper-list-cert {
  border: 2px solid $custom-color-dcdcdc;
  overflow: auto;
  height: 250px;
  border-radius: 5px;
}

.wrapper-list-cert > ul li {
  padding: 6px 10px;
}

.wrapper-list-cert > ul li > div.block {
  display: flex;
  align-content: center;
  align-items: center;
  gap: 10px;
}

.selected-cert,
.wrapper-list-cert > ul li:hover {
  background-color: #cbf6f0;
}

.custom-title {
  display: flex;
  align-content: center;
  align-items: center;
  gap: 10px;
  margin: 10px 0;
}

.modal-content-container {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: space-between;
  overflow: auto;
}

.wrapper-list {
  height: auto;
}

.custom-height {
  height: auto !important;
}
</style>
